/* =============================================================================
 * PDP - Product Group Display (dfa25ozn) — runtime
 *
 * Renders sibling-product swatches on a product detail page. The Liquid emits
 * the full markup + an inline `<script type="application/json">` payload per
 * group block:
 *
 *   {
 *     "groupId": "<uuid>",
 *     "currentGid": "gid://shopify/Product/<numeric>",
 *     "products": [
 *       { "gid": "<gid>", "handle": "<handle>", "url": "/products/<handle>",
 *         "optionValues": { "<optionSlug>": "<value>", ... } },
 *       ...
 *     ]
 *   }
 *
 * The IIFE:
 *   1. Identifies the current selection (option-values of the current product)
 *      from `products[]` where `gid === currentGid`.
 *   2. Marks any swatch whose value can't compose with the current selection in
 *      the other rows as `.is-disabled` (compatibility greying).
 *   3. On click: composes a target selection { ...currentSelection,
 *      [clickedOption]: clickedValue }, finds the matching entry in `products[]`,
 *      and navigates to its `url`. No staged state — each click is a navigation.
 *
 * Analytics:
 *   - `$spectrum:product_groups:swatch_click` — { group_id, option_slug, value,
 *     target_handle }
 *   - `$spectrum:product_groups:swatch_no_match` — { group_id, attempted_selection }
 *
 * No-bind fallback: when `window.__spectrumAi` is absent (theme without the
 * bootstrap SDK), the SSR'd swatches stay visible and clicks still navigate
 * to the resolved product. Analytics silently noop.
 *
 * Trust boundary: backend-supplied payload is trusted by contract. No
 * client-side HTML escaping — every interpolation inside the inline JSON was
 * piped through Shopify's `| json` filter at render time.
 * ============================================================================= */

;(() => {
  const SNIPPET_ID = 'dfa25ozn'
  const FEATURE_SLUG = 'product_groups'
  const ROOT_SELECTOR = `.sai-${SNIPPET_ID}`
  const SWATCH_SELECTOR = `.sai-${SNIPPET_ID}__swatch`
  const GROUP_SELECTOR = `.sai-${SNIPPET_ID}__group`
  const DATA_SELECTOR = `.sai-${SNIPPET_ID}__data`
  const INIT_KEY = `__sai_${SNIPPET_ID}_initialized__`
  const OBSERVER_KEY = `__sai_${SNIPPET_ID}_observer__`
  const NODE_INIT_KEY = `__sai${SNIPPET_ID.charAt(0).toUpperCase() + SNIPPET_ID.slice(1)}Init`

  if (window[INIT_KEY]) return
  window[INIT_KEY] = true

  function noopTrack() {}

  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* swallow — analytics is best-effort */
      }
    }
  }

  function parseGroupPayload(scriptEl) {
    if (!scriptEl) return null
    try {
      const parsed = JSON.parse(scriptEl.textContent || '')
      if (!parsed || !Array.isArray(parsed.products)) return null
      return parsed
    } catch (_) {
      return null
    }
  }

  function findProductByGid(products, gid) {
    if (!gid) return null
    for (const entry of products) {
      if (entry.gid === gid) return entry
    }
    return null
  }

  function findProductByOptionValues(products, selection) {
    for (const entry of products) {
      if (matchesSelection(entry.optionValues, selection)) return entry
    }
    return null
  }

  function matchesSelection(optionValues, selection) {
    if (!optionValues) return false
    for (const key of Object.keys(selection)) {
      if (optionValues[key] !== selection[key]) return false
    }
    return true
  }

  /**
   * Build the current selection from the current product's optionValues.
   * Returns an empty object when the current product is not a member of the
   * group (e.g. the snippet was rendered on a non-member PDP) — clicks then
   * compose selections from scratch.
   */
  function computeCurrentSelection(payload) {
    const current = findProductByGid(payload.products, payload.currentGid)
    return current?.optionValues ? { ...current.optionValues } : {}
  }

  /**
   * Decide which swatches should be dimmed (`.is-disabled`). The cue must match
   * the click handler's behaviour or the UI lies: a swatch shouldn't look
   * disabled if clicking it would actually navigate.
   *
   * Disabled = no product whatsoever carries `optionSlug = value`. The
   * exact-match composition AND the single-value fallback both miss.
   * (Composition-mismatch swatches still navigate via the value-only fallback,
   * so they stay visually enabled.)
   */
  function applyCompatibilityGreying(groupEl, payload) {
    const swatches = groupEl.querySelectorAll(SWATCH_SELECTOR)
    for (const swatch of swatches) {
      const optionSlug = swatch.getAttribute('data-option-slug')
      const value = swatch.getAttribute('data-value')
      if (!optionSlug || value == null) continue

      const valueOnlyMatch = findProductByOptionValues(payload.products, {
        [optionSlug]: value,
      })
      if (!valueOnlyMatch) {
        swatch.classList.add('is-disabled')
        // aria-disabled mirrors the visual cue so assistive tech announces
        // the swatch as unavailable. The button stays focusable on purpose
        // (matches the click handler's swatch_no_match analytics path).
        swatch.setAttribute('aria-disabled', 'true')
      }
    }
  }

  function bindGroupClicks(groupEl, payload, currentSelection, track) {
    const groupId = payload.groupId
    groupEl.addEventListener('click', (event) => {
      // event.target.closest() walks up from the click target. Because this
      // listener is on groupEl, any matching ancestor is guaranteed to be
      // inside groupEl — no additional `groupEl.contains(swatch)` check
      // needed.
      const swatch = event.target.closest(SWATCH_SELECTOR)
      if (!swatch) return

      const optionSlug = swatch.getAttribute('data-option-slug')
      const value = swatch.getAttribute('data-value')
      if (!optionSlug || value == null) return

      // 1. Variant-picker style: keep selections in other rows, switch the
      //    clicked option to the new value, look for the product matching the
      //    full composed selection.
      const target = { ...currentSelection, [optionSlug]: value }
      let match = findProductByOptionValues(payload.products, target)
      let matchedFully = true

      // 2. Single-value fallback: when the composed selection has no exact
      //    product (sparse data), navigate to the first product carrying just
      //    `optionSlug = value`. Preserves clickability; the destination PDP
      //    re-renders the snippet with the new currentSelection, so the user
      //    can keep refining from there.
      if (!match) {
        matchedFully = false
        match = findProductByOptionValues(payload.products, { [optionSlug]: value })
      }

      if (!match || !match.url) {
        track(`${FEATURE_SLUG}:swatch_no_match`, {
          group_id: groupId,
          attempted_selection: target,
        })
        return
      }

      // Don't re-navigate when the user clicks the swatch already representing
      // the current product (no-op).
      if (match.gid === payload.currentGid) return

      // is_oos reflects the *clicked* swatch's state — useful for measuring
      // how often shoppers click sold-out swatches (Liquid keeps them
      // navigable for `faded`/`strikethrough`/`faded-strike` modes).
      track(`${FEATURE_SLUG}:swatch_click`, {
        group_id: groupId,
        option_slug: optionSlug,
        value,
        target_handle: match.handle || '',
        match_kind: matchedFully ? 'exact' : 'value_only',
        is_oos: swatch.classList.contains('is-oos'),
      })

      // Inside Studio's preview iframe, frame-protection.ts virtualises
      // window.location and silently no-ops some navigation paths. Don't
      // navigate from the iframe — preview is an edit surface, not a
      // shopping surface. Storefront pages render at window.top and proceed
      // normally.
      if (window !== window.top) return
      window.location.assign(match.url)
    })
  }

  function initContainer(node, api, wrapper) {
    const groups = node.querySelectorAll(GROUP_SELECTOR)
    if (groups.length === 0) return

    // One track handle per container — bound via __spectrumAi.snippet.bind so
    // the standard envelope (snippet_id, experience_handle, etc.) is auto-attached.
    // The variant callback is intentionally a no-op for this snippet: we don't
    // need to re-render swatches on variant swaps (a different variant might
    // change typography via the CSS baker, but never the option matrix).
    let trackHandle = noopTrack
    if (wrapper && api && typeof api.bind === 'function') {
      const handles = api.bind(wrapper, () => {})
      if (handles && typeof handles.track === 'function') {
        trackHandle = safeTrack(handles.track)
      }
    }

    for (const groupEl of groups) {
      const scriptEl = groupEl.querySelector(DATA_SELECTOR)
      const payload = parseGroupPayload(scriptEl)
      if (!payload) continue

      const currentSelection = computeCurrentSelection(payload)
      applyCompatibilityGreying(groupEl, payload)
      bindGroupClicks(groupEl, payload, currentSelection, trackHandle)
    }
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    // Discover by the snippet's own outer div. Decoupling from the per-instance
    // wrapper's data attribute keeps the runtime working with every wrapper
    // shape: the production `_sai-{iid}.liquid` (data-spectrum-snippet-id +
    // data-spectrum-instance-id), the local dev `_sai-mut-*.liquid`
    // (data-spectrum-lq-snippet only), or any future wrapper variant.
    const containers = document.querySelectorAll(ROOT_SELECTOR)

    for (const node of containers) {
      if (node[NODE_INIT_KEY]) continue
      node[NODE_INIT_KEY] = true
      // Walk up for the Spectrum wrapper that owns the analytics envelope.
      // Both prod (`data-spectrum-instance-id`) and dev (`data-spectrum-lq-snippet`)
      // expose at least one of these. Absent → analytics silently noop.
      const wrapper = node.closest('[data-spectrum-instance-id], [data-spectrum-lq-snippet]')
      initContainer(node, api, wrapper)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }

  // Quick-add modals (Horizon, Dawn variant-picker drawers, etc.) inject the
  // product section via cloneNode/innerHTML — `<script src>` tags inside that
  // markup do NOT auto-execute, so DOMContentLoaded missed those containers.
  // An idempotent MutationObserver picks them up; the per-node init guard
  // (NODE_INIT_KEY) makes repeated calls safe.
  //
  // Disconnect any previously-installed observer first so the IIFE stays
  // idempotent under re-evaluation (test re-runs, hot-reload, etc).
  if (typeof MutationObserver !== 'undefined') {
    const existing = window[OBSERVER_KEY]
    if (existing && typeof existing.disconnect === 'function') existing.disconnect()
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const added of record.addedNodes) {
          if (!(added instanceof HTMLElement)) continue
          if (added.matches?.(ROOT_SELECTOR) || added.querySelector?.(ROOT_SELECTOR)) {
            bindAllContainers()
            return
          }
        }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    window[OBSERVER_KEY] = observer
  }
})()
